"use client";

import { useId, useRef, useState, type DragEvent, type HTMLAttributes, type Ref } from "react";
import { Knopf } from "../knopf";
import { SymbolHochladen, SymbolPlus } from "../zeilen-symbole";
import { Zustandspille } from "../zustandspille";
import { Feld } from "./feld";

/**
 * Datei-Ablage (Musterkatalog M-011 Teil 3, Design-Guide `datei-liste.md`): die Geist-Zeile „Datei anhängen“ unter der
 * Dateiliste. Ein Klick öffnet den Dateidialog (mehrere Dateien möglich); wird eine Datei über den Bereich gezogen, wird die
 * Zeile zur Ablagefläche „Loslassen zum Anhängen“ (Bereich = was `useDateiZiehen` umschließt). Zweite Form `knopf` für
 * den Leerzustand (sekundärer Knopf mit Symbol). Dritte Form `text` (W202 V1, Karte „Anhänge“ im Reiter „Bezüge“ des Aufgaben-Details): Textknopf
 * mit Plus für den Kopf einer Karte (vierte Form `flaeche`, Betreiber 22.10.2026: große gestrichelte Ablagefläche mit Symbol, Titel und freier Unterzeile, wo Hochladen die Hauptaufgabe der Seite ist; Einsatz-Matrix `datei-liste.md`) (Aktion oben rechts); beim Ziehen zeigt er „Loslassen zum Anhängen“. Der Dateidialog ist ein verstecktes natives Feld, das hier (Sperrklinke
 * Regel 14) gebaut wird, nicht beim Aufrufer. Abweichung AW-014 (kein Katalogblock für eine Ablage als Geist-Zeile mit Ziehen).
 *
 * Der Baustein prüft weder Typ noch Größe noch Rechte: `accept` und `hilfe` („Höchstens 20 MB je Datei.“) sagen es dem
 * Nutzer, die verbindliche Prüfung liegt in der Action (SG-1, SG-6). Behebbare Fehler je Datei (zu groß, Typ nicht erlaubt,
 * r100) kommen als `fehler`-Liste und stehen als Pillen am Feld; Fehlschlag und Ablehnung (r105) gehören dem Aufrufer als
 * Hinweis an der Liste, nie hierher.
 */
const GEIST =
  "w-full py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-dashed border-layer-line bg-transparent text-muted-foreground-1 hover:bg-surface-hover hover:text-foreground focus:outline-hidden focus:bg-surface-focus disabled:opacity-50 disabled:pointer-events-none";
const GEIST_ZIEHEND =
  "w-full py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-dashed border-primary bg-primary-100 text-primary-800 focus:outline-hidden focus:bg-primary-200";
// Ziehen-Zustand des Knopfs bleibt eine eigene Form (Ablagefläche); der ruhende Knopf ist `Knopf` (Betreiber 10.10.2026, Welle 2b).
// Fokus wie überall: Hintergrund, kein Ring.
const KNOPF_ZIEHEND =
  "py-1.5 px-3 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-primary bg-primary-100 text-primary-800 focus:outline-hidden focus:bg-primary-200";

// Vierte Form `flaeche` (Betreiber 22.10.2026, „Ja variante B mit folgendem Umbau“): die große gestrichelte Ablagefläche, wo
// Hochladen die Hauptaufgabe der Seite ist (Import, neuer Lauf, Datei-Übergabe). Die ganze Fläche ist ein Knopf (Tastatur,
// Fokus als Hintergrund wie überall, kein Ring); Zustände nur über Theme-Tokens.
const FLAECHE =
  "w-full p-8 sm:p-12 flex flex-col items-center justify-center gap-y-1 text-center rounded-xl border border-dashed border-layer-line bg-layer text-muted-foreground-1 hover:bg-surface-hover focus:outline-hidden focus:bg-surface-focus disabled:opacity-50 disabled:pointer-events-none";
const FLAECHE_ZIEHEND =
  "w-full p-8 sm:p-12 flex flex-col items-center justify-center gap-y-1 text-center rounded-xl border border-dashed border-primary bg-primary-100 text-primary-800 focus:outline-hidden focus:bg-primary-200";
const FLAECHE_FEHLER =
  "w-full p-8 sm:p-12 flex flex-col items-center justify-center gap-y-1 text-center rounded-xl border border-dashed border-destructive bg-layer text-muted-foreground-1 hover:bg-surface-hover focus:outline-hidden focus:bg-surface-focus disabled:opacity-50 disabled:pointer-events-none";

type BereichProps =Pick<HTMLAttributes<HTMLElement>, "onDragEnter" | "onDragOver" | "onDragLeave" | "onDrop">;

/**
 * Ziehen und Ablegen über einem Bereich: `bereichProps` an den umschließenden Container hängen, `ziehend` an die Ablage geben.
 * Reagiert nur auf Dateien (nicht auf Text oder Links) und nur, wenn `aktiv` (Schreibrecht, nicht belegt).
 */
export function useDateiZiehen(onDateien: (dateien: File[]) => void, aktiv: boolean): { ziehend: boolean; bereichProps: BereichProps } {
  const [ziehend, setZiehend] = useState(false);
  const verschachtelt = useRef(0);
  const hatDatei = (e: DragEvent<HTMLElement>) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
  const bereichProps: BereichProps = {
    onDragEnter: (e) => {
      if (!aktiv || !hatDatei(e)) return;
      e.preventDefault();
      verschachtelt.current += 1;
      setZiehend(true);
    },
    onDragOver: (e) => {
      if (!aktiv || !hatDatei(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (e) => {
      if (!aktiv || !hatDatei(e)) return;
      verschachtelt.current = Math.max(0, verschachtelt.current - 1);
      if (verschachtelt.current === 0) setZiehend(false);
    },
    onDrop: (e) => {
      if (!aktiv || !hatDatei(e)) return;
      e.preventDefault();
      verschachtelt.current = 0;
      setZiehend(false);
      const dateien = Array.from(e.dataTransfer.files ?? []);
      if (dateien.length > 0) onDateien(dateien);
    },
  };
  return { ziehend: aktiv && ziehend, bereichProps };
}

export function DateiAblage({
  id,
  label = "Datei anhängen",
  ablegenText = "Loslassen zum Anhängen",
  hilfe,
  accept,
  mehrere = true,
  variante = "zeile",
  titel = "Dateien hierher ziehen oder auswählen",
  unterzeile,
  ziehend = false,
  deaktiviert = false,
  fehler = [],
  knopfRef,
  onDateien,
}: {
  id?: string;
  label?: string;
  ablegenText?: string;
  /** Hilfetext unter der Ablage („Höchstens 20 MB je Datei.“). */
  hilfe?: string;
  accept?: string;
  mehrere?: boolean;
  /** `zeile` = Geist-Zeile unter der Liste, `knopf` = sekundärer Knopf im Leerzustand, `text` = Textknopf im Kopf einer Karte. */
  variante?: "zeile" | "knopf" | "text" | "flaeche";
  /** Nur `flaeche`: Titelzeile der Fläche (beim Ziehen steht `ablegenText`). */
  titel?: string;
  /** Nur `flaeche`: Unterzeile unter dem Titel (erlaubte Typen und Grenzen), frei vom Aufrufer. */
  unterzeile?: string;
  ziehend?: boolean;
  deaktiviert?: boolean;
  /** Behebbare Fehler je Datei (r100), kurze Sätze. */
  fehler?: string[];
  /** Ref auf den Knopf (Fokus nach dem Löschen einer Zeile). */
  knopfRef?: Ref<HTMLButtonElement>;
  onDateien: (dateien: File[]) => void;
}) {
  const eingabe = useRef<HTMLInputElement | null>(null);
  const kennung = useId();
  const flaeche = variante === "flaeche";
  const zeile = variante === "zeile";
  const klasse = zeile ? (ziehend ? GEIST_ZIEHEND : GEIST) : KNOPF_ZIEHEND;
  const text = variante === "text";
  const eigeneForm = zeile || (ziehend && !text);
  return (
    <Feld id={id} label={label} labelVersteckt hilfe={hilfe}>
      {(a) => (
        <>
          {/* Preline: forms/forms-sub-creation-management/forms-cat-file-upload-forms/dropzone-upload-form */}
          <input
            ref={eingabe}
            id={a.id}
            type="file"
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            multiple={mehrere}
            accept={accept}
            disabled={deaktiviert}
            onChange={(e) => {
              const dateien = Array.from(e.target.files ?? []);
              // Zurücksetzen, damit dieselbe Datei erneut gewählt werden kann (z. B. nach einem Fehler).
              e.target.value = "";
              if (dateien.length > 0) onDateien(dateien);
            }}
          />
          {flaeche ? (
            <>
              {/* Preline: forms/forms-sub-creation-management/forms-cat-file-upload-forms/dropzone-upload-form (Illustration durch Lucide „upload“ ersetzt, ganze Fläche als Knopf) */}
              <button
                ref={knopfRef}
                type="button"
                className={ziehend ? FLAECHE_ZIEHEND : fehler.length > 0 ? FLAECHE_FEHLER : FLAECHE}
                disabled={deaktiviert}
                aria-labelledby={`${kennung}-titel`}
                aria-describedby={[unterzeile ? `${kennung}-unter` : null, a["aria-describedby"]].filter(Boolean).join(" ") || undefined}
                onClick={() => eingabe.current?.click()}
              >
                <svg
                  className="shrink-0 size-10 mb-2"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" x2="12" y1="3" y2="15" />
                </svg>
                <span id={`${kennung}-titel`} className={ziehend ? "text-sm/6 font-medium" : "text-sm/6 font-medium text-foreground"}>
                  {ziehend ? ablegenText : titel}
                </span>
                {unterzeile && (
                  <span id={`${kennung}-unter`} className="text-xs text-muted-foreground-1">
                    {unterzeile}
                  </span>
                )}
              </button>
            </>
          ) : eigeneForm ? (
            <button ref={knopfRef} type="button" className={klasse} disabled={deaktiviert} aria-describedby={a["aria-describedby"]} onClick={() => eingabe.current?.click()}>
              {zeile && !ziehend ? <SymbolPlus /> : <SymbolHochladen />}
              {ziehend ? ablegenText : label}
            </button>
          ) : text ? (
            <Knopf ref={knopfRef} variante="text" groesse="xs" symbol={ziehend ? <SymbolHochladen /> : <SymbolPlus />} disabled={deaktiviert} ariaDescribedby={a["aria-describedby"]} onClick={() => eingabe.current?.click()}>
              {ziehend ? ablegenText : label}
            </Knopf>
          ) : (
            <Knopf ref={knopfRef} groesse="sm" symbol={<SymbolHochladen />} disabled={deaktiviert} ariaDescribedby={a["aria-describedby"]} onClick={() => eingabe.current?.click()}>
              {label}
            </Knopf>
          )}
          {fehler.length > 0 && (
            <ul className="mt-2 space-y-1.5" aria-live="polite">
              {fehler.map((satz, i) => (
                <li key={`${i}-${satz}`}>
                  <Zustandspille ton="gefahr" mitPunkt>
                    {satz}
                  </Zustandspille>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Feld>
  );
}
